import { useMemo } from "react";

import { AssessmentStatus } from "api/api-data-types";
import { ASSESSMENT_VIEW_QUERY_PARAM } from "components/constants";
import { AssessmentPublishStatusKey } from "i18n/i18next";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import { pageSelectors, selectCurrentSectionId } from "store/selectors";
import { selectAllAssessmentInfo } from "store/selectors/computed";
import { useQueryParamsManager } from "utils/routing";

export const useIsAssessing = () => {
  const { getQueryParam } = useQueryParamsManager();
  const { canAuthorSection } = useCapabilities();

  if (!canAuthorSection) {
    return false;
  }
  // Instructors can assess, so check the query param
  // to see if they are currently assessing
  const isAssessing = getQueryParam(ASSESSMENT_VIEW_QUERY_PARAM);
  return !!isAssessing;
};

export type AllAssessmentDashboardDataType = {
  allAssessmentsCSVData: {
    headers: string[];
    rows: (string | number)[][];
  };
  allAssessmentsTableRows: Array<{
    id: string;
    hidden: boolean;
    title: string;
    topicTitle: string | null | undefined;
    dueAt: string | null;
    submissionsLastCollectedAt: string | null;
    status: AssessmentStatus;
    unassessedLateSubmissionsCount: number;
    unseenLateSubmissionsCount: number;
    unseenSubmissionsCount: number;
    assessedSubmissionsCount: number;
    totalSubmissionsCount: number;
    isAssessmentComplete: boolean;
    isMarkedComplete: boolean;
    publishStatus: AssessmentPublishStatusKey;
    assessedCount: number;
    assessmentCategory: string;
    avgPoints: string;
    outcomesCount: number;
    outcomesTooltip: string;
    lastSubmittedDate: string;
    progress: string;
  }>;
};

export const useLateSubmissionCounts = () => {
  const sectionId = useAppSelector(selectCurrentSectionId);
  const allAssessmentInfo = useAppSelector(selectAllAssessmentInfo);
  const pages = useAppSelector(pageSelectors.selectEntities);

  return useMemo(() => {
    let totalLateCount = 0;
    let newLateCount = 0;

    for (const pageId of Object.keys(allAssessmentInfo)) {
      const info = allAssessmentInfo[pageId];
      const page = pages[pageId];
      if (page && !page.assessing_marked_complete_at && page.section_id === sectionId) {
        totalLateCount += info.unassessed_late_submissions_count;
        newLateCount += info.unseen_late_submissions_count;
      }
    }

    return { totalLateCount, newLateCount };
  }, [allAssessmentInfo, pages, sectionId]);
};
